import { Button, Icon, InputItem, Tooltip } from "pepsico-ds";
import PropTypes from "prop-types";
import { useState } from "react";
import LimitTypeSelectorModal from "./LimitTypeSelectorModal";
import { DEFAULT_WIN_LIMIT_FIELDS } from "./constants";

const WinLimitsFields = ({
  title,
  subtitle,
  limits = {},
  onChange,
  disabled = false,
  limitTypes,
}) => {
  // Use provided limitTypes or fall back to defaults
  const winLimitFields =
    limitTypes && limitTypes.length > 0 ? limitTypes : DEFAULT_WIN_LIMIT_FIELDS;
  const [showLimitModal, setShowLimitModal] = useState(false);
  const preventNumberScroll = (e) => {
    e.currentTarget.blur();
  };

  const handleChange = (key, value) => {
    if (value === "") {
      onChange({
        ...limits,
        [key]: "",
      });
      return;
    }
    const parsed = parseInt(value, 10);
    onChange({
      ...limits,
      [key]: isNaN(parsed) ? "" : parsed,
    });
  };

  const handleAddLimit = (limitType) => {
    const newLimits = {
      ...limits,
      [limitType.key]: "",
    };
    onChange(newLimits);
  };

  const handleRemoveLimit = (limitKey) => {
    const newLimits = { ...limits };
    delete newLimits[limitKey];
    onChange(newLimits);
  };

  const activeLimits = winLimitFields.filter((field) => {
    const value = limits[field.key];
    // Show only if value is a number or empty string (just added)
    return typeof value === "number" || value === "";
  });

  const availableLimits = winLimitFields.filter((field) => {
    const value = limits[field.key];
    // Available if null or undefined
    return value === null || value === undefined;
  });

  return (
    <div className="win-limits-section">
      <div className="display-flex justify-content-space-between align-items-center mb-3">
        <div>
          {title && <p className="font-semibold">{title}</p>}
          {subtitle && <p className="font-xs">{subtitle}</p>}
        </div>
        {!disabled && availableLimits.length > 0 && (
          <Button
            text="Add Limit"
            iconLeading="add"
            variant="tertiary"
            size="small"
            onClick={() => setShowLimitModal(true)}
          />
        )}
      </div>
      <div className="form-row form-row-thirds">
        {activeLimits.map(({ key, label }) => (
          <div className="form-col-third" key={key}>
            <div className="form-field-wrapper">
              <label className="form-field-label">{label}</label>
              <div className="input-with-delete">
                <div className="input-wrapper">
                  <InputItem
                    type="number"
                    size="medium"
                    value={limits[key] ?? ""}
                    onChange={(e) => handleChange(key, e.target.value)}
                    onWheel={preventNumberScroll}
                    disabled={disabled}
                  />
                </div>
                {!disabled && (
                  <Tooltip text="Remove limit" position="top">
                    <button
                      className="icon-button delete-button"
                      onClick={() => handleRemoveLimit(key)}
                      type="button"
                    >
                      <Icon icon="delete" size="small" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        ))}
        {activeLimits.length === 0 && (
          <div className="form-col-full">
            <p className="text-muted">
              No limits configured. Click &quot;Add Limit&quot; to add one.
            </p>
          </div>
        )}
      </div>

      <LimitTypeSelectorModal
        show={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitTypes={availableLimits}
        onSelectLimit={handleAddLimit}
        title="Add Per-User Win Limit"
      />
    </div>
  );
};

WinLimitsFields.propTypes = {
  title: PropTypes.string,
  limits: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  limitTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
};

export default WinLimitsFields;
